// DESCRIPTION: Pure date picker calendar component for booking flows.
// Displays a month grid with available dates highlighted and allows single date selection.

import React, { useState, useMemo } from 'react';
import { __ } from '@wordpress/i18n';

const DAY_LABELS_SUN = [ 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa' ];
const DAY_LABELS_MON = [ 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su' ];

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Convert a Date to YYYY-MM-DD string.
 *
 * @param {Date} d
 *
 * @return {string}
 */
function toISO( d ) {
    return [
        d.getFullYear(),
        String( d.getMonth() + 1 ).padStart( 2, '0' ),
        String( d.getDate() ).padStart( 2, '0' ),
    ].join( '-' );
}

/**
 * Get the first cell date for a month grid.
 *
 * @param {number} year
 * @param {number} month    0-indexed month.
 * @param {number} firstDay 0 = Sunday, 1 = Monday.
 *
 * @return {Date}
 */
function getGridStart( year, month, firstDay ) {
    const first = new Date( year, month, 1 );
    let dayOfWeek = first.getDay();

    if ( firstDay === 1 ) {
        dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    }

    const start = new Date( first );
    start.setDate( start.getDate() - dayOfWeek );

    return start;
}

/**
 * DatePicker — a pure, reusable mini-calendar for selecting a single date.
 *
 * @param {Object}   props
 * @param {string[]} props.availableDates Array of ISO date strings (YYYY-MM-DD) that are selectable.
 * @param {string}   props.selectedDate   Currently selected ISO date string, or ''.
 * @param {Function} props.onDateSelect   Called with ISO date string when user picks a date.
 * @param {string}   [props.minDate]      Earliest selectable date (ISO string). Defaults to today.
 * @param {string}   [props.maxDate]      Latest selectable date (ISO string). No limit if omitted.
 * @param {number}   [props.firstDay=0]   0 = Sunday start, 1 = Monday start.
 * @param {boolean}  [props.loading=false] Show loading indicator over the grid.
 * @param {string}   [props.className=''] Additional CSS class names.
 */
const DatePicker = ( {
    availableDates = [],
    selectedDate = '',
    onDateSelect,
    minDate = '',
    maxDate = '',
    firstDay = 0,
    loading = false,
    className = '',
} ) => {
    const today = useMemo( () => toISO( new Date() ), [] );
    const effectiveMin = minDate || today;

    // Determine initial month to display: selectedDate's month, or minDate's month, or today.
    const initialAnchor = useMemo( () => {
        const ref = selectedDate || effectiveMin;
        const parts = ref.split( '-' ).map( Number );

        return { year: parts[ 0 ], month: parts[ 1 ] - 1 };
    }, [] ); // eslint-disable-line react-hooks/exhaustive-deps

    const [ year, setYear ] = useState( initialAnchor.year );
    const [ month, setMonth ] = useState( initialAnchor.month );

    const availableSet = useMemo( () => new Set( availableDates ), [ availableDates ] );
    const dayLabels = firstDay === 1 ? DAY_LABELS_MON : DAY_LABELS_SUN;

    // Build 42 grid cells.
    const cells = useMemo( () => {
        const gridStart = getGridStart( year, month, firstDay );

        return Array.from( { length: 42 }, ( _, i ) => {
            const d = new Date( gridStart );
            d.setDate( d.getDate() + i );

            return d;
        } );
    }, [ year, month, firstDay ] );

    const monthYear = `${ year }-${ String( month + 1 ).padStart( 2, '0' ) }`;

    const goToPrev = () => {
        if ( month === 0 ) {
            setYear( ( y ) => y - 1 );
            setMonth( 11 );
        } else {
            setMonth( ( m ) => m - 1 );
        }
    };

    const goToNext = () => {
        if ( month === 11 ) {
            setYear( ( y ) => y + 1 );
            setMonth( 0 );
        } else {
            setMonth( ( m ) => m + 1 );
        }
    };

    // Disable prev button if previous month is entirely before minDate.
    const prevMonthEnd = month === 0
        ? `${ year - 1 }-12-31`
        : `${ year }-${ String( month ).padStart( 2, '0' ) }-31`;
    const canGoPrev = prevMonthEnd >= effectiveMin;

    // Disable next button if maxDate is set and next month starts after it.
    const nextMonthStart = month === 11
        ? `${ year + 1 }-01-01`
        : `${ year }-${ String( month + 2 ).padStart( 2, '0' ) }-01`;
    const canGoNext = ! maxDate || nextMonthStart <= maxDate;

    return (
        <div className={ `nb-date-picker ${ className }`.trim() }>
            {/* Month/Year header with navigation */}
            <div className="nb-date-picker__nav">
                <button
                    type="button"
                    className="nb-date-picker__nav-btn"
                    onClick={ goToPrev }
                    disabled={ ! canGoPrev }
                    aria-label={ __( 'Previous month', 'nemtly-booking' ) }
                >
                    <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ 2 } d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                <span className="nb-date-picker__title">
                    { MONTH_NAMES[ month ] } { year }
                </span>

                <button
                    type="button"
                    className="nb-date-picker__nav-btn"
                    onClick={ goToNext }
                    disabled={ ! canGoNext }
                    aria-label={ __( 'Next month', 'nemtly-booking' ) }
                >
                    <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ 2 } d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {/* Day-of-week labels */}
            <div className="nb-date-picker__header">
                { dayLabels.map( ( label, i ) => (
                    <div key={ i } className="nb-date-picker__day-label">{ label }</div>
                ) ) }
            </div>

            {/* Date grid */}
            <div className={ `nb-date-picker__grid ${ loading ? 'nb-date-picker__grid--loading' : '' }` }>
                { cells.map( ( date ) => {
                    const iso = toISO( date );
                    const isOutside = ! iso.startsWith( monthYear );
                    const isToday = iso === today;
                    const isSelected = iso === selectedDate;
                    const isAvailable = availableSet.has( iso );
                    const isPast = iso < effectiveMin;
                    const isBeyondMax = maxDate && iso > maxDate;
                    const isDisabled = isOutside || isPast || isBeyondMax || ! isAvailable;

                    const cellClasses = [
                        'nb-date-picker__cell',
                        isOutside ? 'nb-date-picker__cell--outside' : '',
                        isToday ? 'nb-date-picker__cell--today' : '',
                        isSelected ? 'nb-date-picker__cell--selected' : '',
                        isAvailable && ! isOutside && ! isPast ? 'nb-date-picker__cell--available' : '',
                        isDisabled ? 'nb-date-picker__cell--disabled' : '',
                    ].filter( Boolean ).join( ' ' );

                    return (
                        <button
                            key={ iso }
                            type="button"
                            className={ cellClasses }
                            onClick={ () => ! isDisabled && onDateSelect( iso ) }
                            disabled={ isDisabled }
                            aria-label={ iso }
                            aria-current={ isToday ? 'date' : undefined }
                            aria-pressed={ isSelected }
                        >
                            <span className="nb-date-picker__date">{ date.getDate() }</span>
                            { isAvailable && ! isOutside && (
                                <span className="nb-date-picker__dot" aria-hidden="true" />
                            ) }
                        </button>
                    );
                } ) }
            </div>

            { loading && (
                <div className="nb-date-picker__loading" aria-live="polite">
                    <span className="nb-date-picker__spinner" />
                </div>
            ) }
        </div>
    );
};

export default DatePicker;
